import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/20/solid';

import { useShop } from '@services/ShopProvider';
import { useEnabledChannels, useChannelDefinitions } from '@services/channels';

import { JourneyCard } from '../../components/JourneyCard';

import styles from './ChannelsCard.module.scss';

/**
 * Wired to real channel state via useEnabledChannels(). Lists every channel
 * available to the org with a green check for enabled, grey ring for not.
 * Each row links to that channel's management page; comingSoon betas are
 * filtered out unless already enabled.
 */
export const ChannelsCard = () => {
	const { data: shops, shop } = useShop();
	const enabledChannelsRaw = useEnabledChannels();
	const enabledChannels = enabledChannelsRaw ?? [];
	const { data: channelDefinitions } = useChannelDefinitions(undefined, { suspense: false });

	const scopeShopId = shop?.id ?? shops?.[0]?.id;
	const scopeShops = shop?.id ? [shop] : (shops ?? []);
	const isLoading = enabledChannelsRaw === undefined || !channelDefinitions;
	const enabledIds = new Set(enabledChannels.map((c) => c.id));
	const availableMap = new Map();
	for (const candidate of scopeShops) {
		const available = candidate.availableChannels ?? {};
		for (const [id, meta] of Object.entries(available)) {
			if (availableMap.has(id) || !meta) continue;
			// Drop beta/feed-only channels from the list — they're not actionable
			// from this card and pad the list with noise. Keep them if enabled
			// (so a retailer can see what they have on).
			if (meta.comingSoon && !enabledIds.has(id)) continue;
			const def = channelDefinitions?.[id];
			availableMap.set(id, {
				id,
				name: def?.title ?? meta.label ?? meta.name ?? id,
				iconUrl: def?.iconUrl ?? null
			});
		}
	}
	const channelList = [...availableMap.values()];

	const enabledCount = channelList.filter((c) => enabledIds.has(c.id)).length;

	const manageHref = (id) => (scopeShopId ? `/channels/${scopeShopId}/manage/${id}` : '/channels');

	return (
		<JourneyCard
			title="Channels"
			subtitle={isLoading ? ' ' : `${enabledCount} of ${channelList.length} channels enabled`}
			ctaLabel="Manage channels"
			ctaHref="/channels"
		>
			{isLoading ? (
				<ChannelsCardSkeleton />
			) : (
				<ul className={styles.list}>
					{channelList.map((channel) => {
						const isEnabled = enabledIds.has(channel.id);
						return (
							<li key={channel.id}>
								<Link to={manageHref(channel.id)} className={styles.item}>
									{isEnabled ? (
										<span className={styles.checkPill} aria-label="Enabled">
											<CheckIcon className={styles.checkIcon} />
										</span>
									) : (
										<span className={`${styles.checkPill} ${styles.checkPillEmpty}`} aria-label="Not enabled" />
									)}
									{channel.iconUrl && (
										<img src={channel.iconUrl} alt="" aria-hidden="true" className={styles.channelIcon} />
									)}
									<span className={styles.name}>{channel.name}</span>
									<ArrowRightIcon className={styles.arrow} aria-hidden="true" />
								</Link>
							</li>
						);
					})}
				</ul>
			)}
		</JourneyCard>
	);
};

const ChannelsCardSkeleton = () => (
	<ul className={styles.list}>
		{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
			<li key={i}>
				<div className={styles.item}>
					<span className={`${styles.checkPill} ${styles.checkPillEmpty} ${styles.skeletonPill}`} />
					<span className={`${styles.channelIcon} ${styles.skeletonIcon}`} />
					<span className={`${styles.name} ${styles.skeletonName}`} />
				</div>
			</li>
		))}
	</ul>
);
